import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile,
  IfilterProps, IhelpTicket, Isuccess, TbackEndGeneratedFields,
  TbackEndGeneratedFieldsExcludingId
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type representing a help ticket request.
 */
export type ThelpTicketRequest<
    CreateMode extends boolean = false, U = Omit<Partial<IhelpTicket>, TbackEndGeneratedFieldsExcludingId>> =
    CreateMode extends true ? Omit<IhelpTicket, TbackEndGeneratedFields> : U;

/**
 * Class representing a help ticket.
 */
export class HelpTicket
  extends DatabaseAuto {
  /**
   * The base URL for the help ticket.
   */
  static baseUrl = '/helpticket';

  /**
   * The user ID.
   */
  commerceUser: IhelpTicket['commerceUser'];

  /**
   * The company ID.
   */
  company: IhelpTicket['company'];

  /**
   * The title of the help ticket.
   */
  title: IhelpTicket['title'];

  /**
   * The description of the help ticket.
   */
  description: IhelpTicket['description'];

  /**
   * The status of the help ticket.
   */
  status: IhelpTicket['status'];

  /**
   * The handler ID.
   */
  handler?: IhelpTicket['handler'];

  constructor(data: IhelpTicket) {
    super(data);
    this.commerceUser = data.commerceUser;
    this.company = data.company;
    this.title = data.title;
    this.description = data.description;
    this.status = data.status;
    this.handler = data.handler;
  }

  /**
   * Retrieves a list of help tickets.
   *
   * @param {number} [offset=0] - The offset to start retrieving help tickets from.
   * @param {number} [limit=0] - The limit of help tickets to retrieve.
   *
   * @returns {Promise<{count: number, dataArr: HelpTicket[]}>} - A promise
   *  that resolves to an object containing the total count of help tickets
   * and an array of help ticket objects.
   */
  static async getAll(offset = 0, limit = 0) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IhelpTicket>>(`${HelpTicket.baseUrl}?${query}`);
    const helpTickets = await lastValueFrom(observer$);

    return {
      count: helpTickets.count,
      dataArr: helpTickets.data
        .map(val => new HelpTicket(val)) };
  }

  /**
   * Retrieves a list of help tickets based on a filter.
   *
   * @param {IfilterProps} filter - The filter to apply to the help tickets.
   *
   * @returns {Promise<{count: number, dataArr: HelpTicket[]}>} - A promise
   *  that resolves to an object containing the total count of help tickets
   * and an array of help ticket objects.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IhelpTicket>>(`${HelpTicket.baseUrl}/filter`, filter);
    const helpTickets = await lastValueFrom(observer$);

    return {
      count: helpTickets.count,
      dataArr: helpTickets.data
        .map(val => new HelpTicket(val)) };
  }

  /**
   * Retrieves a help ticket by ID.
   *
   * @param {string} resourceId - The ID of the help ticket to retrieve.
   *
   * @returns {Promise<HelpTicket>} - A promise that resolves to a help ticket object.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IhelpTicket>(`${HelpTicket.baseUrl}/${resourceId}`);
    const helpTicket = await lastValueFrom(observer$);

    return new HelpTicket(helpTicket);
  }

  /**
   * Retrieves the current user's profile.
   *
   * @returns {Promise<HelpTicket>} - A promise that resolves to a help ticket object.
   */
  static async myProfile() {
    const observer$ = CommerceClient.ehttp
      .makePost<IhelpTicket>(`${HelpTicket.baseUrl}/my-profile`, {});
    const helpTicket = await lastValueFrom(observer$);

    return new HelpTicket(helpTicket);
  }

  /**
   * Deletes multiple help tickets based on the provided IDs.
   *
   * @param {IbulkDelete} vals - The IDs of the help tickets to delete.
   *
   * @returns {Promise<IbulkDeleteSuccessResponse>} - A promise that resolves to a success object.
   */
  static bulkDelete(vals: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${HelpTicket.baseUrl}/bulk-delete`, vals);

    return lastValueFrom(observer$);
  }

  /**
   * Validates a help ticket invitation token.
   * @param {Object} payload - The payload containing the token to validate.
   * @param {string} payload.token - The invitation token to validate.
   * @returns {Promise<Isuccess>} The result of the validation operation.
   */
  static validateInvitation(payload: { token: string }): Promise<Isuccess> {
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${HelpTicket.baseUrl}/validate-invitation`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Adds a new help ticket.
   *
   * @param {ThelpTicketRequest<true>} payload - The payload containing the help ticket data.
   *
   * @returns {Promise<HelpTicket>} - A promise that resolves to a help ticket object.
   */
  static async add(payload: ThelpTicketRequest<true>) {
    const observer$ = CommerceClient.ehttp.makePost<IhelpTicket>(`${HelpTicket.baseUrl}`, payload);

    return new HelpTicket(await lastValueFrom(observer$));
  }

  /**
   * Uploads multiple files to the staff member.
   *
   * @param {Ifile[]} files - The files to upload.
   * @returns {Promise<Isuccess>} The result of the file upload operation.
   */
  uploadImages(files: Ifile[]) {
    return CommerceClient.ehttp
      .uploadFiles<IhelpTicket>(files, `${HelpTicket.baseUrl}/upload-files`, { _id: this._id });
  }

  /**
   * Updates the help ticket.
   *
   * @param {ThelpTicketRequest} payload - The payload containing the help ticket data.
   *
   * @returns {Promise<HelpTicket>} - A promise that resolves to a help ticket object.
   */
  async update(payload: ThelpTicketRequest) {
    const observer$ = CommerceClient.ehttp.makePut<IhelpTicket>(`${HelpTicket.baseUrl}`, payload);

    return new HelpTicket(await lastValueFrom(observer$));
  }

  /**
   * Removes the help ticket.
   *
   * @returns {Promise<IdeleteOneResponse | null>} - A promise that resolves to a delete one response object.
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${HelpTicket.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
